import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

if (!import.meta.env.VITE_BACKEND_URL) {
  throw new Error('VITE_BACKEND_URL environment variable is required');
}
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

// ============================================
// Types
// ============================================

export interface ReferralConfig {
  id: string;
  default_discount_percentage: number;
  discount_type: string;
  default_commission_percentage: number;
  vat_rate: number;
  minimum_payout_threshold: number;
  payout_currency: string;
  payout_frequency: string;
  lifetime_commission: boolean;
  max_referrals_per_code: number | null;
  code_expiry_days: number | null;
  system_enabled: boolean;
  require_admin_approval: boolean;
  created_at: string;
  updated_at: string;
}

export interface PractitionerInfo {
  id: string;
  email: string;
  full_name: string | null;
}

export interface ReferralCode {
  id: string;
  code: string;
  practitioner_id: string;
  practitioner: PractitionerInfo | null;
  status: 'pending' | 'active' | 'suspended' | 'revoked';
  approved_by: string | null;
  approver: PractitionerInfo | null;
  approved_at: string | null;
  custom_discount_percentage: number | null;
  custom_commission_percentage: number | null;
  custom_expiry_date: string | null;
  custom_max_referrals: number | null;
  total_uses: number;
  total_revenue_generated: number;
  total_commission_earned: number;
  admin_notes: string | null;
  suspension_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface Commission {
  id: string;
  practitioner_id: string;
  practitioner: PractitionerInfo | null;
  referral_code_id: string;
  referred_user_id: string;
  referred_user: PractitionerInfo | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  gross_amount: number;
  discount_applied: number;
  net_amount: number;
  commission_percentage: number;
  gross_commission: number;
  vat_rate: number;
  vat_amount: number;
  net_commission: number;
  status: 'pending' | 'approved' | 'paid' | 'cancelled';
  payout_id: string | null;
  transaction_type: string;
  currency: string;
  transaction_date: string;
  created_at: string;
  updated_at: string;
}

export interface Payout {
  id: string;
  practitioner_id: string;
  practitioner: PractitionerInfo | null;
  total_commission: number;
  commission_count: number;
  payout_method: string;
  stripe_payout_id: string | null;
  stripe_connected_account_id: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  failure_reason: string | null;
  retry_count: number;
  last_retry_at: string | null;
  scheduled_date: string;
  processed_at: string | null;
  completed_at: string | null;
  processed_by: string | null;
  processor: PractitionerInfo | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommissionSummary {
  total_commissions: number;
  total_gross_amount: number;
  total_net_commission: number;
  pending_commission: number;
  approved_commission: number;
  paid_commission: number;
}

export interface AdminStats {
  total_active_codes: number;
  total_pending_codes: number;
  paying_referrals: number; // Users who signed up with code AND made payment
  total_revenue_generated: number;
  total_commissions_paid: number;
  total_commissions_pending: number;
  pending_payout_amount: number;
  top_performers: any[];
}

export interface PractitionerEarnings {
  practitioner_id: string;
  referral_code: string;
  code_status: string;
  paying_referrals: number; // Users who signed up with your code AND made payment
  total_revenue_generated: number;
  total_commission_earned: number;
  pending_commission: number;
  approved_commission: number;
  paid_commission: number;
  total_payouts: number;
  last_payout_date: string | null;
  recent_commissions: Commission[];
  recent_payouts: Payout[];
}

// ============================================
// Config Hooks
// ============================================

export function useReferralConfig() {
  return useQuery({
    queryKey: ['referral-config'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/referrals/config`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch referral config');
      }

      return response.json() as Promise<ReferralConfig>;
    },
    enabled: true,
  });
}

export function useUpdateReferralConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<ReferralConfig>) => {
      const response = await fetch(`${API_BASE_URL}/referrals/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to update referral config');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-config'] });
      toast.success('Referral configuration updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update config: ${error.message}`);
    },
  });
}

// ============================================
// Referral Code Hooks
// ============================================

export function useReferralCodes(statusFilter?: string, practitionerId?: string) {

  return useQuery({
    queryKey: ['referral-codes', statusFilter, practitionerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);
      if (practitionerId) params.append('practitioner_id', practitionerId);

      const response = await fetch(
        `${API_BASE_URL}/referrals/codes?${params.toString()}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch referral codes');
      }

      return response.json() as Promise<ReferralCode[]>;
    },
    enabled: true,
  });
}

export function useCreateReferralCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      code: string;
      practitioner_id: string;
      custom_discount_percentage?: number;
      custom_commission_percentage?: number;
      custom_expiry_date?: string;
      custom_max_referrals?: number;
      admin_notes?: string;
    }) => {
      const response = await fetch(`${API_BASE_URL}/referrals/codes`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create referral code');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-codes'] });
      toast.success('Referral code created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create code: ${error.message}`);
    },
  });
}

export function useUpdateReferralCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      codeId,
      data,
    }: {
      codeId: string;
      data: {
        status?: string;
        custom_discount_percentage?: number;
        custom_commission_percentage?: number;
        custom_expiry_date?: string;
        custom_max_referrals?: number;
        admin_notes?: string;
        suspension_reason?: string;
      };
    }) => {
      const response = await fetch(`${API_BASE_URL}/referrals/codes/${codeId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to update referral code');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-codes'] });
      toast.success('Referral code updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update code: ${error.message}`);
    },
  });
}

export function useDeleteReferralCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (codeId: string) => {
      const response = await fetch(`${API_BASE_URL}/referrals/codes/${codeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete referral code');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-codes'] });
      toast.success('Referral code deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete code: ${error.message}`);
    },
  });
}

// ============================================
// Commission Hooks
// ============================================

export function useCommissions(practitionerId?: string, statusFilter?: string) {

  return useQuery({
    queryKey: ['commissions', practitionerId, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (practitionerId) params.append('practitioner_id', practitionerId);
      if (statusFilter) params.append('status_filter', statusFilter);

      const response = await fetch(
        `${API_BASE_URL}/referrals/commissions?${params.toString()}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch commissions');
      }

      return response.json() as Promise<Commission[]>;
    },
    enabled: true,
  });
}

export function useCommissionSummary(practitionerId?: string) {

  return useQuery({
    queryKey: ['commission-summary', practitionerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (practitionerId) params.append('practitioner_id', practitionerId);

      const response = await fetch(
        `${API_BASE_URL}/referrals/commissions/summary?${params.toString()}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch commission summary');
      }

      return response.json() as Promise<CommissionSummary>;
    },
    enabled: true,
  });
}

export function useApproveCommission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commissionId: string) => {
      const response = await fetch(
        `${API_BASE_URL}/referrals/commissions/${commissionId}/approve`,
        {
          method: 'PUT',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve commission');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      queryClient.invalidateQueries({ queryKey: ['commission-summary'] });
      toast.success('Commission approved');
    },
    onError: (error: Error) => {
      toast.error(`Failed to approve: ${error.message}`);
    },
  });
}

// ============================================
// Payout Hooks
// ============================================

export function usePayouts(practitionerId?: string, statusFilter?: string) {

  return useQuery({
    queryKey: ['payouts', practitionerId, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (practitionerId) params.append('practitioner_id', practitionerId);
      if (statusFilter) params.append('status_filter', statusFilter);

      const response = await fetch(
        `${API_BASE_URL}/referrals/payouts?${params.toString()}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payouts');
      }

      return response.json() as Promise<Payout[]>;
    },
    enabled: true,
  });
}

export function useCreatePayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      practitioner_id: string;
      scheduled_date?: string;
      admin_notes?: string;
    }) => {
      const response = await fetch(`${API_BASE_URL}/referrals/payouts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create payout');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      toast.success('Payout created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create payout: ${error.message}`);
    },
  });
}

export function useProcessPayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payoutId: string) => {
      const response = await fetch(
        `${API_BASE_URL}/referrals/payouts/${payoutId}/process`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to process payout');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      toast.success('Payout processed successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to process payout: ${error.message}`);
    },
  });
}

export function useCancelPayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ payoutId, reason }: { payoutId: string; reason?: string }) => {
      const params = new URLSearchParams();
      if (reason) params.append('reason', reason);

      const response = await fetch(
        `${API_BASE_URL}/referrals/payouts/${payoutId}?${params.toString()}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel payout');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      toast.success('Payout cancelled');
    },
    onError: (error: Error) => {
      toast.error(`Failed to cancel payout: ${error.message}`);
    },
  });
}

// ============================================
// Dashboard Hooks
// ============================================

export function useAdminReferralStats() {

  return useQuery({
    queryKey: ['admin-referral-stats'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/referrals/admin/stats`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch admin stats');
      }

      return response.json() as Promise<AdminStats>;
    },
    enabled: true,
  });
}

export function usePractitionerEarnings() {

  return useQuery({
    queryKey: ['practitioner-earnings'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/referrals/practitioner/earnings`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch practitioner earnings');
      }

      return response.json() as Promise<PractitionerEarnings>;
    },
    enabled: true,
  });
}

// ============================================
// User Referral Code Hooks
// ============================================

export function useValidateReferralCode() {

  return useMutation({
    mutationFn: async (code: string) => {
      const response = await fetch(`${API_BASE_URL}/referrals/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        throw new Error('Failed to validate code');
      }

      return response.json();
    },
  });
}

export function useApplyReferralCode() {

  return useMutation({
    mutationFn: async (code: string) => {
      const response = await fetch(`${API_BASE_URL}/referrals/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to apply code');
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Referral code applied successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
